package lacaravana.app

import kotlinx.coroutines.await
import kotlin.js.Promise

@JsModule("@vlcn.io/crsqlite-wasm")
@JsNonModule
external object CrSqliteModule {
    val default: dynamic
}

private fun initCrSqlite(): Promise<dynamic> =
    CrSqliteModule.default.unsafeCast<() -> Promise<dynamic>>()()

class Database private constructor(private val db: dynamic) {

    suspend fun exec(sql: String) {
        (db.exec(sql) as Promise<dynamic>).await()
    }

    suspend fun query(sql: String, params: Array<Any?> = emptyArray()): List<List<Any?>> {
        @Suppress("UNCHECKED_CAST")
        val result = (db.execA(sql, params) as Promise<Array<Array<Any?>>>).await()
        return result.map { it.toList() }
    }

    var applyingChanges = false

    suspend fun execute(sql: String, params: Array<Any?>) {
        (db.execA(sql, params) as Promise<dynamic>).await()
        if (!applyingChanges) lacaravana.sync.NatsSync.pushChanges()
    }

    suspend fun siteId(): String {
        val result = query("SELECT quote(crsql_site_id())")
        return result[0][0] as String
    }

    suspend fun dbVersion(): Long {
        val result = query("SELECT crsql_db_version()")
        return (result[0][0] as Number).toLong()
    }

    suspend fun changesSince(version: Long, excludeSite: String? = null): List<List<Any?>> {
        val versionJs = version.toDouble()
        return if (excludeSite != null) {
            query(
                "SELECT * FROM crsql_changes WHERE db_version > ? AND site_id != ?",
                arrayOf(versionJs, excludeSite)
            )
        } else {
            query("SELECT * FROM crsql_changes WHERE db_version > ?", arrayOf(versionJs))
        }
    }

    suspend fun applyChanges(changes: List<List<Any?>>) {
        applyingChanges = true
        try {
            for (change in changes) {
                execute(
                    "INSERT INTO crsql_changes VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)",
                    change.toTypedArray()
                )
            }
        } finally {
            applyingChanges = false
        }
    }

    suspend fun close() {
        (db.close() as Promise<dynamic>).await()
    }

    private suspend fun initSchema() {
        exec("""CREATE TABLE IF NOT EXISTS mesas (
            id INTEGER NOT NULL PRIMARY KEY, numero INTEGER NOT NULL DEFAULT 0,
            estado TEXT NOT NULL DEFAULT 'libre', capacidad INTEGER NOT NULL DEFAULT 4,
            updated_at TEXT NOT NULL DEFAULT (datetime('now')))""")
        exec("SELECT crsql_as_crr('mesas')")

        exec("""CREATE TABLE IF NOT EXISTS menu_items (
            id TEXT NOT NULL PRIMARY KEY, nombre TEXT NOT NULL DEFAULT '',
            categoria TEXT NOT NULL DEFAULT '', precio_cents INTEGER NOT NULL DEFAULT 0,
            disponible INTEGER NOT NULL DEFAULT 1, orden INTEGER NOT NULL DEFAULT 0,
            updated_at TEXT NOT NULL DEFAULT (datetime('now')))""")
        exec("SELECT crsql_as_crr('menu_items')")

        exec("""CREATE TABLE IF NOT EXISTS pedidos (
            id TEXT NOT NULL PRIMARY KEY, mesa_id INTEGER NOT NULL DEFAULT 0,
            estado TEXT NOT NULL DEFAULT 'abierto',
            created_at TEXT NOT NULL DEFAULT (datetime('now')),
            updated_at TEXT NOT NULL DEFAULT (datetime('now')))""")
        exec("SELECT crsql_as_crr('pedidos')")

        exec("""CREATE TABLE IF NOT EXISTS pedido_items (
            id TEXT NOT NULL PRIMARY KEY, pedido_id TEXT NOT NULL DEFAULT '',
            menu_item_id TEXT NOT NULL DEFAULT '',
            cantidad INTEGER NOT NULL DEFAULT 1, notas TEXT,
            estado TEXT NOT NULL DEFAULT 'pendiente',
            updated_at TEXT NOT NULL DEFAULT (datetime('now')))""")
        exec("SELECT crsql_as_crr('pedido_items')")

        exec("""CREATE TABLE IF NOT EXISTS caja (
            id TEXT NOT NULL PRIMARY KEY, fecha TEXT NOT NULL DEFAULT (datetime('now')),
            turno INTEGER NOT NULL DEFAULT 1,
            estado TEXT NOT NULL DEFAULT 'abierta', total_cents INTEGER NOT NULL DEFAULT 0,
            updated_at TEXT NOT NULL DEFAULT (datetime('now')))""")
        exec("SELECT crsql_as_crr('caja')")

        exec("""CREATE TABLE IF NOT EXISTS tickets (
            id TEXT NOT NULL PRIMARY KEY, pedido_id TEXT NOT NULL DEFAULT '',
            caja_id TEXT NOT NULL DEFAULT '', total_cents INTEGER NOT NULL DEFAULT 0,
            metodo_pago TEXT NOT NULL DEFAULT '',
            created_at TEXT NOT NULL DEFAULT (datetime('now')))""")
        exec("SELECT crsql_as_crr('tickets')")

        exec("""CREATE TABLE IF NOT EXISTS stock (
            menu_item_id TEXT NOT NULL PRIMARY KEY,
            cantidad INTEGER NOT NULL DEFAULT 0,
            updated_at TEXT NOT NULL DEFAULT (datetime('now')))""")
        exec("SELECT crsql_as_crr('stock')")

        exec("""CREATE TABLE IF NOT EXISTS config (
            key TEXT NOT NULL PRIMARY KEY, value TEXT NOT NULL DEFAULT '')""")
        exec("SELECT crsql_as_crr('config')")

        seedDefaults()
    }

    private suspend fun seedDefaults() {
        val count = query("SELECT COUNT(*) FROM mesas")
        if ((count[0][0] as Number).toInt() == 0) {
            for (i in 1..8) {
                execute(
                    "INSERT INTO mesas (id, numero, estado, capacidad) VALUES (?, ?, 'libre', 4)",
                    arrayOf(i, i)
                )
            }
        }
    }

    companion object {
        suspend fun open(name: String = "lacaravana"): Database {
            val sqlite = initCrSqlite().await()
            val db = (sqlite.open(name) as Promise<dynamic>).await()
            return Database(db).also { it.initSchema() }
        }
    }
}
